import React, { useEffect, useMemo, useState } from 'react';
import { Row, Col, Container, Spinner, Alert, Button } from 'reactstrap';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import * as aiAgentsActions from '../../store/aiAgents/actions';
import * as acquisitionOrdersActions from '../../store/acquisitionOrders/actions';
import * as campaignsActions from '../../store/campaigns/actions';
import * as leadFieldsActions from '../../store/leadFields/actions';
import * as monetizationOrdersActions from '../../store/monetizationOrders/actions';
import * as aiSimulationActions from '../../store/aiSimulation/actions';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import SimulatorSetup from '../../components/AiAgents/SimulatorSetup';
import SimulatorChat from '../../components/AiAgents/SimulatorChat';
import SimulatorPanel from '../../components/AiAgents/SimulatorPanel';
import SimulatorRecap from '../../components/AiAgents/SimulatorRecap';
import { buildRecap } from '../../components/AiAgents/simulationRecap';
import { isSessionValid } from '../../components/AiAgents/simulationSession';
import { actualCountry } from '../../selectors/country';

const Simulator = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const aiAgents = useSelector((state) => state.AiAgent.aiAgents);
  const acquisitionOrders = useSelector(
    (state) => state.AcquisitionOrder.acquisitionOrders,
  );
  const campaigns = useSelector((state) => state.Campaign.campaigns);
  const leadFields = useSelector((state) => state.LeadField.leadFields);
  // Only the recap needs these, and only to turn the ids a positive closure
  // carries into names a colleague can read.
  const monetizationOrders = useSelector(
    (state) => state.MonetizationOrder.monetizationOrders,
  );
  const simulation = useSelector((state) => state.AiSimulation);
  // The agent speaks the country's language and reasons on its clock: the
  // setup says which, and the engine feed dates its decisions on it.
  const country = useSelector(actualCountry);

  useEffect(() => {
    dispatch(aiSimulationActions.clearError());
    dispatch(aiAgentsActions.syncAiAgents());
    dispatch(acquisitionOrdersActions.syncAcquisitionOrders());
    dispatch(campaignsActions.syncCampaigns());
    dispatch(leadFieldsActions.syncLeadFields());
    dispatch(monetizationOrdersActions.syncMonetizationOrders());
  }, [dispatch]);

  const agent = aiAgents.find((candidate) => candidate.id === id);

  const orders = useMemo(
    () =>
      acquisitionOrders.filter(
        (order) => (order.aiRouting || {}).agentId === id,
      ),
    [acquisitionOrders, id],
  );

  const campaignsByOrderId = useMemo(
    () =>
      campaigns.reduce(
        (acc, campaign) => ({
          ...acc,
          [campaign.acquisitionOrderId]: [
            ...(acc[campaign.acquisitionOrderId] || []),
            campaign,
          ],
        }),
        {},
      ),
    [campaigns],
  );

  // A session from an earlier visit is only resumable while the configuration
  // it was taken against has not moved. The overlay inside it sits on top of
  // live reads, so a stale one shows a mixture of two configurations.
  useEffect(() => {
    if (!agent || !simulation.session) return;
    const stillValid = isSessionValid(simulation.session, {
      agentId: id,
      acquisitionOrderId: (simulation.target || {}).acquisitionOrderId,
      agentUpdatedAtMs: agent.updatedAt
        ? new Date(agent.updatedAt).getTime()
        : 0,
    });
    if (!stillValid) dispatch(aiSimulationActions.discardSimulation());
  }, [agent, id, simulation.session, simulation.target, dispatch]);

  const closed = !!(simulation.status && simulation.status.closed);

  // Opens itself the moment the conversation is over, and closes for good on
  // the colleague's click. `closed` only turns true once per conversation, so
  // a discard followed by a new run opens it again. Reloading the page on an
  // already-closed simulation reopens it too, which is the right side to err
  // on: the recap is the most useful thing on screen at that point, and the
  // chat header carries a link to open it again by hand.
  const [recapOpen, setRecapOpen] = useState(false);
  useEffect(() => {
    if (closed) setRecapOpen(true);
  }, [closed]);

  const recap = useMemo(
    () =>
      closed
        ? buildRecap({
            status: simulation.status,
            cost: simulation.cost,
            messages: simulation.messages,
            events: simulation.events,
            monetizationOrders,
          })
        : null,
    [
      closed,
      simulation.status,
      simulation.cost,
      simulation.messages,
      simulation.events,
      monetizationOrders,
    ],
  );

  if (!agent)
    return (
      <div className="page-content">
        <Container fluid>
          <Spinner color="primary" />
        </Container>
      </div>
    );

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs
          title={t('AI Agents')}
          breadcrumbItem={`${t('Simulator')} — ${agent.name}`}
        />
        {!!simulation.error && <Alert color="danger">{simulation.error}</Alert>}
        <Row>
          <Col lg={12}>
            {!simulation.session ? (
              <SimulatorSetup
                agent={agent}
                country={country}
                acquisitionOrders={orders}
                campaignsByOrderId={campaignsByOrderId}
                leadFields={leadFields}
                loading={simulation.loading}
                onStart={({ acquisitionOrderId, lead, mode }) =>
                  dispatch(
                    aiSimulationActions.startSimulation({
                      agentId: id,
                      acquisitionOrderId,
                      lead,
                      mode,
                    }),
                  )
                }
              />
            ) : (
              <Row>
                <Col lg={7}>
                  {closed && !recapOpen && (
                    <div className="mb-2">
                      <Button
                        color="link"
                        className="p-0"
                        onClick={() => setRecapOpen(true)}
                      >
                        {t('Show the recap again')}
                      </Button>
                    </div>
                  )}
                  <SimulatorChat
                    messages={simulation.messages}
                    status={simulation.status}
                    cost={simulation.cost}
                    turnsLeft={simulation.turnsLeft}
                    loading={simulation.loading}
                    onSend={(text) =>
                      dispatch(aiSimulationActions.replySimulation(text))
                    }
                    onAdvance={(minutes) =>
                      dispatch(aiSimulationActions.advanceSimulation(minutes))
                    }
                    onDiscard={() =>
                      dispatch(aiSimulationActions.discardSimulation())
                    }
                  />
                </Col>
                <Col lg={5}>
                  <SimulatorPanel
                    events={simulation.events}
                    timezone={country?.timezone}
                  />
                </Col>
              </Row>
            )}
          </Col>
        </Row>
        <SimulatorRecap
          recap={recap}
          isOpen={closed && recapOpen}
          toggle={() => setRecapOpen(false)}
        />
      </Container>
    </div>
  );
};

export default Simulator;
